import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import type { ComparisonResult } from "./PathwayComparison";

interface ComparisonStatisticsProps {
  result: ComparisonResult;
}

type PathwayTableRow = {
  pathwayValue: string;
  label: string;
  genes: string[];
  geneCount: number;
  sharedInPathway: number;
};

type TableSortKey = "pathway" | "geneCount" | "sharedGenes";

function escapeCsvCell(v: string): string {
  if (v.includes(",") || v.includes('"') || v.includes("\n")) return `"${v.replace(/"/g, '""')}"`;
  return v;
}

export const ComparisonStatistics: React.FC<ComparisonStatisticsProps> = ({ result }) => {
  const {
    comparisonGeneList,
    oneHopEdges,
    sharedGenes,
    genesPerPathway,
    pathwayLabels,
  } = result;
  const totalUniqueGenes = comparisonGeneList.length;
  const sharedCount = sharedGenes.size;
  const geneGeneEdges = oneHopEdges.length;
  const tableRows = useMemo<PathwayTableRow[]>(() => {
    return Object.keys(genesPerPathway).map((pathwayValue) => {
      const genes = genesPerPathway[pathwayValue] ?? [];
      const sharedInPathway = genes.filter((g) => sharedGenes.has(g)).length;
      const label = pathwayLabels[pathwayValue] ?? pathwayValue;
      return {
        pathwayValue,
        label,
        genes,
        geneCount: genes.length,
        sharedInPathway,
      };
    });
  }, [genesPerPathway, pathwayLabels, sharedGenes]);

  const [tableSearchQuery, setTableSearchQuery] = useState("");
  const [tableSortKey, setTableSortKey] = useState<TableSortKey>("pathway");
  const [tableSortDir, setTableSortDir] = useState<"asc" | "desc">("asc");
  const [isTableFullscreen, setIsTableFullscreen] = useState(false);
  const tableFullscreenRef = useRef<HTMLDivElement>(null);

  const handleSortColumn = useCallback(
    (key: TableSortKey) => {
      if (tableSortKey === key) {
        setTableSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setTableSortKey(key);
        setTableSortDir(key === "pathway" ? "asc" : "desc");
      }
    },
    [tableSortKey]
  );

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsTableFullscreen(document.fullscreenElement === tableFullscreenRef.current);
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  const filteredTableRows = useMemo(() => {
    const q = tableSearchQuery.trim().toLowerCase();
    if (!q) return tableRows;
    return tableRows.filter((row) => {
      if (row.label.toLowerCase().includes(q) || row.pathwayValue.toLowerCase().includes(q))
        return true;
      if (String(row.geneCount).includes(q) || String(row.sharedInPathway).includes(q))
        return true;
      return row.genes.some((g) => g.toLowerCase().includes(q));
    });
  }, [tableRows, tableSearchQuery]);

  const sortedFilteredRows = useMemo(() => {
    const rows = [...filteredTableRows];
    rows.sort((a, b) => {
      let cmp = 0;
      if (tableSortKey === "pathway") {
        cmp = a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
        if (cmp === 0) cmp = a.pathwayValue.localeCompare(b.pathwayValue);
      } else if (tableSortKey === "geneCount") {
        cmp = a.geneCount - b.geneCount;
        if (cmp === 0) cmp = a.label.localeCompare(b.label, undefined, { sensitivity: "base" });
      } else {
        cmp = a.sharedInPathway - b.sharedInPathway;
        if (cmp === 0) cmp = a.geneCount - b.geneCount;
      }
      return tableSortDir === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [filteredTableRows, tableSortKey, tableSortDir]);

  /** Fixed-width slot so column width stays stable when sort changes */
  const sortGlyph = (key: TableSortKey) => {
    const active = tableSortKey === key;
    return (
      <span
        className="inline-flex w-4 h-4 shrink-0 items-center justify-center text-blue-600 dark:text-blue-400"
        aria-hidden
      >
        {active &&
          (tableSortDir === "asc" ? (
            <ChevronUpIcon className="h-4 w-4" />
          ) : (
            <ChevronDownIcon className="h-4 w-4" />
          ))}
      </span>
    );
  };

  const sortHeaderBtnClass =
    "inline-flex items-center gap-1 max-w-full rounded px-0 py-0.5 hover:bg-gray-200/80 dark:hover:bg-slate-600/80 outline-none focus:outline-none ring-0 focus:ring-0 border-0 bg-transparent cursor-pointer text-inherit font-semibold uppercase tracking-wider text-xs text-gray-700 dark:text-gray-200";

  const ariaSortFor = (key: TableSortKey): "ascending" | "descending" | "none" =>
    tableSortKey === key ? (tableSortDir === "asc" ? "ascending" : "descending") : "none";

  const handleTableFullscreen = useCallback(() => {
    const el = tableFullscreenRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  const handleDownloadPathwayTableCsv = useCallback(() => {
    if (tableRows.length === 0) return;
    const header =
      "Pathway ID,Pathway,Gene count,Shared genes count,Genes\n";
    const rows = tableRows.map((row) => {
      const genesStr = row.genes.join("; ");
      return [
        escapeCsvCell(row.pathwayValue),
        escapeCsvCell(row.label),
        row.geneCount,
        row.sharedInPathway,
        escapeCsvCell(genesStr),
      ].join(",");
    });
    const csv = header + rows.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "pathway-comparison-genes-per-pathway.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [tableRows]);

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
        Comparison Statistics
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">Total Unique Genes</p>
          <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            {totalUniqueGenes}
          </p>
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">Shared Genes (≥2 pathways)</p>
          <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{sharedCount}</p>
        </div>
        <div className="rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">Gene–Gene Edges</p>
          <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            {geneGeneEdges}
          </p>
        </div>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
          Genes per pathway
        </h4>
        <div
          ref={tableFullscreenRef}
          className={`relative rounded-lg border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 overflow-hidden min-w-0 max-w-full ${isTableFullscreen ? "border-0" : "w-full"}`}
          style={{
            width: isTableFullscreen ? "100vw" : undefined,
            height: isTableFullscreen ? "100vh" : undefined,
            minHeight: isTableFullscreen ? "100vh" : undefined,
          }}
        >
          <div className="flex flex-wrap items-center justify-end gap-2 p-2 border-b border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700/50 min-w-0 shrink-0">
            <div className="relative min-w-[180px] max-w-sm flex-1 sm:flex-initial">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-slate-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search table..."
                value={tableSearchQuery}
                onChange={(e) => setTableSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 dark:border-slate-500 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <button
              type="button"
              onClick={handleDownloadPathwayTableCsv}
              title="Download as CSV"
              className="p-2 rounded-lg border border-gray-300 dark:border-slate-500 bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={handleTableFullscreen}
              title={isTableFullscreen ? "Exit fullscreen" : "Fullscreen"}
              className="p-2 rounded-lg border border-gray-300 dark:border-slate-500 bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
            >
              {isTableFullscreen ? (
                <ArrowsPointingInIcon className="h-4 w-4" />
              ) : (
                <ArrowsPointingOutIcon className="h-4 w-4" />
              )}
            </button>
          </div>
          <div
            className="overflow-x-auto overflow-y-auto min-w-0 max-h-[min(420px,50vh)]"
            style={isTableFullscreen ? { height: "calc(100vh - 52px)", maxHeight: "none" } : undefined}
          >
            <table className="min-w-full table-fixed divide-y divide-gray-200 dark:divide-slate-600">
              <colgroup>
                <col className="min-w-0" />
                <col className="w-[7.5rem]" />
                <col className="w-[8.5rem]" />
              </colgroup>
              <thead className="bg-gray-50 dark:bg-slate-700 sticky top-0 z-10">
                <tr>
                  <th
                    scope="col"
                    aria-sort={ariaSortFor("pathway")}
                    className="px-4 py-2 text-left align-top"
                  >
                    <button
                      type="button"
                      onClick={() => handleSortColumn("pathway")}
                      onMouseDown={(e) => e.preventDefault()}
                      className={`${sortHeaderBtnClass} text-left w-full min-w-0`}
                    >
                      <span className="truncate min-w-0 text-left">Pathway</span>
                      {sortGlyph("pathway")}
                    </button>
                  </th>
                  <th
                    scope="col"
                    aria-sort={ariaSortFor("geneCount")}
                    className="px-4 py-2 text-left align-top whitespace-nowrap"
                  >
                    <button
                      type="button"
                      onClick={() => handleSortColumn("geneCount")}
                      onMouseDown={(e) => e.preventDefault()}
                      className={`${sortHeaderBtnClass} whitespace-nowrap`}
                    >
                      Gene Count
                      {sortGlyph("geneCount")}
                    </button>
                  </th>
                  <th
                    scope="col"
                    aria-sort={ariaSortFor("sharedGenes")}
                    className="px-4 py-2 text-left align-top whitespace-nowrap"
                  >
                    <button
                      type="button"
                      onClick={() => handleSortColumn("sharedGenes")}
                      onMouseDown={(e) => e.preventDefault()}
                      className={`${sortHeaderBtnClass} whitespace-nowrap`}
                    >
                      Shared Genes
                      {sortGlyph("sharedGenes")}
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-600">
                {sortedFilteredRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
                    >
                      {tableSearchQuery.trim()
                        ? `No rows match "${tableSearchQuery.trim()}".`
                        : "No pathway data."}
                    </td>
                  </tr>
                ) : (
                  sortedFilteredRows.map((row) => (
                    <tr key={row.pathwayValue}>
                      <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                        {row.label}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap tabular-nums">
                        {row.geneCount}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap tabular-nums">
                        {row.sharedInPathway}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
